import { useCallback, useMemo, useRef, useState } from 'react';

import { MAX_VISIT_SERVICES } from '@borradh-workspace/contracts';

import {
  APPOINTMENT_NO_PRACTITIONER,
  type AppointmentAdditionalService,
  type AppointmentCreatePractitioner,
  canPractitionerPerformService,
} from './appointment-create-form';

/** Services after the first one a visit may hold. */
export const MAX_ADDITIONAL_SERVICES = MAX_VISIT_SERVICES - 1;

export interface AdditionalServicesSelection {
  items: AppointmentAdditionalService[];
  /** Per-row "Service is required", keyed by row key; set by `validate`. */
  errors: Record<string, string>;
  canAdd: boolean;
  /** Add a row, preset to this team member when given. */
  add: (practitionerId?: string) => void;
  /**
   * Add a row for this service — the desktop Service combobox's tick. The team
   * member is kept only if they perform the service.
   */
  addService: (serviceId: string, practitionerId?: string) => void;
  remove: (key: string) => void;
  setService: (key: string, serviceId: string) => void;
  setPractitioner: (key: string, practitionerId: string) => void;
  reset: () => void;
  /** Marks rows without a service; true when every row has one. */
  validate: () => boolean;
}

/**
 * The services a desktop booking adds after the first, each with its own team
 * member. Kept beside the form rather than in it so the first service stays on
 * the shared `appointmentCreateForm` that the mobile funnels also use — a
 * booking with one service is exactly the booking it always was.
 *
 * Changing a row's service drops its team member when they do not perform the
 * new service, so a row never shows a pairing the server would refuse.
 */
export function useAdditionalServices(
  practitioners: readonly AppointmentCreatePractitioner[]
): AdditionalServicesSelection {
  const [items, setItems] = useState<AppointmentAdditionalService[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nextKey = useRef(0);

  const add = useCallback((practitionerId?: string) => {
    nextKey.current += 1;
    const key = `additional-service-${nextKey.current}`;
    setItems((current) =>
      current.length >= MAX_ADDITIONAL_SERVICES
        ? current
        : [
            ...current,
            {
              key,
              serviceId: '',
              practitionerId: practitionerId || APPOINTMENT_NO_PRACTITIONER,
            },
          ]
    );
  }, []);

  const addService = useCallback(
    (serviceId: string, practitionerId?: string) => {
      nextKey.current += 1;
      const key = `additional-service-${nextKey.current}`;
      const keep =
        practitionerId &&
        canPractitionerPerformService(practitioners, serviceId, practitionerId);
      setItems((current) =>
        current.length >= MAX_ADDITIONAL_SERVICES
          ? current
          : [
              ...current,
              {
                key,
                serviceId,
                practitionerId: keep
                  ? practitionerId
                  : APPOINTMENT_NO_PRACTITIONER,
              },
            ]
      );
    },
    [practitioners]
  );

  const remove = useCallback((key: string) => {
    setItems((current) => current.filter((item) => item.key !== key));
    setErrors(({ [key]: _removed, ...rest }) => rest);
  }, []);

  const setService = useCallback(
    (key: string, serviceId: string) => {
      setItems((current) =>
        current.map((item) =>
          item.key !== key
            ? item
            : {
                ...item,
                serviceId,
                // Only drop someone we can see does not do it: before the
                // team list has loaded, keep the pick and let the server judge.
                practitionerId:
                  practitioners.some((p) => p.id === item.practitionerId) &&
                  !canPractitionerPerformService(
                    practitioners,
                    serviceId,
                    item.practitionerId
                  )
                    ? APPOINTMENT_NO_PRACTITIONER
                    : item.practitionerId,
              }
        )
      );
      setErrors(({ [key]: _cleared, ...rest }) => rest);
    },
    [practitioners]
  );

  const setPractitioner = useCallback((key: string, practitionerId: string) => {
    setItems((current) =>
      current.map((item) =>
        item.key === key ? { ...item, practitionerId } : item
      )
    );
  }, []);

  const reset = useCallback(() => {
    setItems([]);
    setErrors({});
  }, []);

  const validate = useCallback(() => {
    const missing = Object.fromEntries(
      items
        .filter((item) => !item.serviceId)
        .map((item) => [item.key, 'Service is required'])
    );
    setErrors(missing);
    return Object.keys(missing).length === 0;
  }, [items]);

  return useMemo(
    () => ({
      items,
      errors,
      canAdd: items.length < MAX_ADDITIONAL_SERVICES,
      add,
      addService,
      remove,
      setService,
      setPractitioner,
      reset,
      validate,
    }),
    [
      items,
      errors,
      add,
      addService,
      remove,
      setService,
      setPractitioner,
      reset,
      validate,
    ]
  );
}
